import React, {useState, useEffect, useRef} from "react";
import "./MindMatch2.css";

import ResultPage from "../../Components/Games/ResultPage.jsx";
import StartGame from "../../Components/Games/StartGame.jsx";
import overlay from "../../assets/img/overlay.png";
import GameIntroScreen from "../../Components/Games/GameIntroScreen.jsx";
import mindMatchIcon from "../../assets/img/icon-Mind-Match.png";
import {lifestyleApi} from "../../apiService/api";
import {getUrlParams} from "../../apiService/timespam";
import { useGameBackNavigation } from "../../Components/Games/useGameBackNavigation";

const EMOJIS = ["🚀", "🧠", "🌈", "💎", "🔥", "⚡", "🤖", "👾"];

const createDeck = () => {
  const pair = [...EMOJIS, ...EMOJIS];

  for (let i = pair.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pair[i], pair[j]] = [pair[j], pair[i]];
  }

  return pair.map((emoji, i) => ({
    id: i,
    emoji,
    flipped: false,
    matched: false,
  }));
};

export default function MindMatch2() {
  useGameBackNavigation();
  const [phase, setPhase] = useState("INTRO");

  /* GAME STATE */

  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [moves, setMoves] = useState(0);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [busy, setBusy] = useState(false);

  /* RESULT STATS */

  const [correctMatches, setCorrectMatches] = useState(0);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [fastResponses, setFastResponses] = useState(0);

  const [goqiiBest, setGoqiiBest] = useState(0);
  const [backendCognitiveScore, setBackendCognitiveScore] = useState(null);
  const [backendSkill, setBackendSkill] = useState("");
  const [weightages, setWeightages] = useState({
    attemptWeightage: 1,
    accuracyWeightage: 1,
    streakWeightage: 1,
    fastWeightage: 1,
    scoreWeightage: 1,
    baseQuestionsAttempt: 0,
    correctMinQuestion: 1,
    streakMinQuestion: 1,
    fastResponseMinQuestion: 1,
    scorePoints: 1,
  });
  const [configError, setConfigError] = useState(false);
  const [cid, setCid] = useState("");
  const [lastInsertedId, setLastInsertedId] = useState(null);

  const gameStartTimeRef = useRef(0);
  const fastRef = useRef(0);

  /* ---------------- TUTORIAL ---------------- */

  const [tutorialCards, setTutorialCards] = useState([
    {emoji: "🚀", flipped: false, matched: false},
    {emoji: "🧠", flipped: false, matched: false},
    {emoji: "🚀", flipped: false, matched: false},
    {emoji: "🧠", flipped: false, matched: false},
  ]);

  const [tutorialFlipped, setTutorialFlipped] = useState([]);
  const [tutorialText, setTutorialText] = useState("Flip a card");
  const [shake, setShake] = useState([]);

  /* ---------------- FETCH BEST ---------------- */

  const fetchGameConfig = () => {
    setConfigError(false);
    const {encryptedId, gid} = getUrlParams();

    lifestyleApi
      .fetchGameDetails(encryptedId, gid)
      .then((res) => {
        if (res.data?.code === 200) {
          const d = res.data.data;
          setBestScore(d.bestScore);
          setGoqiiBest(d.goqiiBest);
          setWeightages({
            attemptWeightage: d.attemptWeightage ?? 1,
            accuracyWeightage: d.accuracyWeightage ?? 1,
            streakWeightage: d.streakWeightage ?? 1,
            fastWeightage: d.fastWeightage ?? 1,
            scoreWeightage: d.scoreWeightage ?? 1,
            baseQuestionsAttempt: d.baseQuestionsAttempt || 0,
            correctMinQuestion: d.correctMinQuestion || 1,
            streakMinQuestion: d.streakMinQuestion || 1,
            fastResponseMinQuestion: d.fastResponseMinQuestion || 1,
            scorePoints: d.scorePoints || 1,
          });
        } else {
          setConfigError(true);
        }
      })
      .catch((err) => {
        console.error("fetchGameDetails failed", err);
        setConfigError(true);
      });
  };

  useEffect(() => {
    fetchGameConfig();
  }, []);

  /* ---------------- READY → GAME ---------------- */

  const startActualGame = async () => {
    const {encryptedId, gid} = getUrlParams();

    try {
      const res = await lifestyleApi.initialGameScore(encryptedId, gid);
      if (res.data?.code === 200) {
        const data = res.data.data;
        setCid(data.cid);
        setLastInsertedId(data.lastInsertedId);
      }
    } catch (err) {
      console.error("initialGameScore failed", err);
    }

    setPhase("READY");

    setTimeout(() => {
      gameStartTimeRef.current = Date.now();

      setCards(createDeck());
      setFlipped([]);
      setMoves(0);
      setScore(0);
      setCorrectMatches(0);
      setWrongAttempts(0);
      setCurrentStreak(0);
      setMaxStreak(0);
      setFastResponses(0);
      fastRef.current = 0;
      setBusy(false);

      setPhase("GAME");
    }, 2000);
  };

  /* ---------------- GAME CLICK ---------------- */

  const handleCellClick = (idx) => {
    if (
      busy ||
      cards[idx].flipped ||
      cards[idx].matched ||
      flipped.length === 2
    )
      return;

    const updated = [...cards];
    updated[idx].flipped = true;

    setCards(updated);

    const next = [...flipped, idx];
    setFlipped(next);

    if (next.length === 2) {
      setMoves((m) => m + 1);
      setBusy(true);

      const [a, b] = next;

      if (updated[a].emoji === updated[b].emoji) {
        setTimeout(() => {
          setCards((prev) =>
            prev.map((c, i) => (next.includes(i) ? {...c, matched: true} : c)),
          );

          const streakBonus = weightages.streakWeightage * 10 * currentStreak;
          const gained = weightages.scorePoints + streakBonus;
          setScore((s) => Math.max(0, s + gained));
          setCorrectMatches((p) => p + 1);

          const streak = currentStreak + 1;
          setCurrentStreak(streak);
          setMaxStreak((prev) => Math.max(prev, streak));

          setFlipped([]);
          setBusy(false);
        }, 500);
      } else {
        setTimeout(() => {
          setCards((prev) =>
            prev.map((c, i) => (next.includes(i) ? {...c, flipped: false} : c)),
          );

          setScore((s) =>
            Math.max(0, s - Math.round(weightages.scorePoints / 3.5)),
          );
          setWrongAttempts((p) => p + 1);
          setCurrentStreak(0);

          setFlipped([]);
          setBusy(false);
        }, 800);
      }
    }
  };

  /* ---------------- GAME COMPLETE ---------------- */

  useEffect(() => {
    if (phase === "GAME" && cards.length > 0 && cards.every((c) => c.matched)) {
      const totalTime = (Date.now() - gameStartTimeRef.current) / 1000;
      const safeMoves = Math.max(1, moves);

      const efficiency = 8 / safeMoves;
      const speedFactor = Math.max(0, 60 / totalTime);

      let fast = 0;

      if (totalTime <= 100 && safeMoves <= 32) {
        fast = Math.round(
          efficiency * 10 * (speedFactor > 1 ? 1.2 : speedFactor),
        );
      }

      fast = Math.min(fast, 10);

      fastRef.current = fast;
      setFastResponses(fast);
      setBestScore((prev) => Math.max(prev, score));

      setTimeout(() => setPhase("RESULT"), 1000);
    }
  }, [cards]);

  /* ---------------- TUTORIAL CLICK ---------------- */

  const handleTutorialClick = (index) => {
    if (
      tutorialCards[index].flipped ||
      tutorialCards[index].matched ||
      tutorialFlipped.length === 2
    )
      return;

    let updated = [...tutorialCards];
    updated[index].flipped = true;

    setTutorialCards(updated);

    const next = [...tutorialFlipped, index];
    setTutorialFlipped(next);

    if (next.length === 1) {
      setTutorialText("Flip another card");
    }

    if (next.length === 2) {
      const [a, b] = next;

      if (updated[a].emoji === updated[b].emoji) {
        setTutorialText("Correct! Find next pair");

        setTimeout(() => {
          updated[a].matched = true;
          updated[b].matched = true;

          setTutorialCards([...updated]);
          setTutorialFlipped([]);

          if (updated.every((c) => c.matched)) {
            setTutorialText("Great! Game starting...");

            setTimeout(() => {
              startActualGame();
            }, 1200);
          }
        }, 500);
      } else {
        setTutorialText("Not a match. Try again");
        setShake([a, b]);

        setTimeout(() => {
          updated[a].flipped = false;
          updated[b].flipped = false;

          setTutorialCards([...updated]);
          setTutorialFlipped([]);
          setShake([]);

          setTutorialText("Flip a card");
        }, 900);
      }
    }
  };

  /* ---------------- SAVE SCORE ---------------- */

  const saveFinalGameScore = async () => {
    const {encryptedId, gid} = getUrlParams();
    const totalQuestions = correctMatches + wrongAttempts;

    try {
      const res = await lifestyleApi.saveGameScore(
        encryptedId,
        gid,
        totalQuestions,
        correctMatches,
        wrongAttempts,
        maxStreak,
        fastRef.current,
        score,
        cid,
        lastInsertedId,
      );
      if (res?.data?.data?.cognitiveScore !== undefined) {
        setBackendCognitiveScore(res.data.data.cognitiveScore);
      }
      if (res?.data?.data?.skill) {
        setBackendSkill(res.data.data.skill);
      }
    } catch (err) {
      console.error("saveGameScore failed", err);
    }
  };

  useEffect(() => {
    if (phase === "RESULT") {
      saveFinalGameScore();
    }
  }, [phase]);

  /* ---------------- QUOTE ---------------- */

  const getQuote = () => {
    if (backendCognitiveScore >= 90) return "Photographic memory!";
    if (backendCognitiveScore >= 75) return "Great memory!";
    if (backendCognitiveScore >= 60) return "Good focus.";
    if (backendCognitiveScore >= 40) return "Nice effort.";

    return "Keep training!";
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="mind-match-root">
      {configError && (
        <div className="api-error-overlay">
          <div className="api-error-card">
            <div className="api-error-title">Something went wrong</div>
            <div className="api-error-text">
              There is a server error to open this game. Please try again after
              some time.
            </div>
            <button className="api-error-retry-btn" onClick={fetchGameConfig}>
              Retry
            </button>
          </div>
        </div>
      )}

      {phase === "INTRO" && (
        <GameIntroScreen
          backgroundImage={overlay}
          gameLogo={mindMatchIcon}
          title="Mind Match"
          onStart={() => setPhase("BENEFITS")}
        />
      )}

      {phase === "BENEFITS" && (
        <StartGame
          backgroundImage={overlay}
          hbHeading="Visual Memory"
          hbDescription="Training your brain to recognize patterns improves recall speed."
          yourBest={bestScore}
          goqiiBest={goqiiBest}
          onStart={() => setPhase("TUTORIAL")}
        />
      )}

      {phase === "TUTORIAL" && (
        <div className="tutorial-screen">
          <div className="tutorial-frame">
            <div className="tutorial-header">
              <div className="tutorial-badge">TUTORIAL</div>

              <button className="tutorial-skip" onClick={startActualGame}>
                Skip
              </button>
            </div>

            <div className="tutorial-watch">{tutorialText}</div>

            <div className="tutorial-grid">
              {tutorialCards.map((card, i) => (
                <div
                  key={i}
                  className={`card
${card.flipped || card.matched ? "flipped" : ""}
${card.matched ? "correct" : ""}
${shake.includes(i) ? "shake" : ""}`}
                  onClick={() => handleTutorialClick(i)}>
                  <div className="card-inner">
                    <div className="face back" />
                    <div className="face front">{card.emoji}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {phase === "READY" && (
        <div className="ready-overlay">
          <div className="ready-text">Ready</div>
        </div>
      )}

      {phase === "GAME" && (
        <div className="game-container">
          <div className="top-bar">
            <div className="stat">
              <span className="stat-label">Points</span>
              <span className="stat-value">{score}</span>
            </div>

            <div className="stat right">
              <span className="stat-label">Moves</span>
              <span className="stat-value">{moves}</span>
            </div>
          </div>

          <div className="grid-area">
            <div className="grid">
              {cards.map((card, i) => (
                <div
                  key={card.id}
                  className={`card ${card.flipped || card.matched ? "flipped" : ""} ${card.matched ? "matched" : ""}`}
                  onClick={() => handleCellClick(i)}>
                  <div className="card-inner">
                    <div className="face back" />
                    <div className="face front">{card.emoji}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bottom-bar">
            <button
              className="btn-restart"
              onClick={() => setPhase("TUTORIAL")}>
              Back to Tutorial
            </button>
          </div>
        </div>
      )}

      {phase === "RESULT" && (
        <ResultPage
          visible={true}
          score={score}
          best={bestScore}
          correct={correctMatches}
          wrong={wrongAttempts}
          longestStreak={maxStreak}
          fastResponses={fastResponses}
          cognitiveScore={backendCognitiveScore}
          domainLabel={backendSkill}
          onReplay={startActualGame}
          quote={getQuote()}
        />
      )}
    </div>
  );
}
